import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const JLPTPreparation: React.FC = () => {
  const [activeLevel, setActiveLevel] = useState<number | null>(null);
  const [hoveredLevel, setHoveredLevel] = useState<number | null>(null);

  const levels = [
    {
      title: "N1",
      description: "Advanced Japanese proficiency for academic and professional settings",
      icon: "🎯",
      link: "/jlpt/n1",
      color: "from-red-500 to-pink-600",
      features: [
        "Advanced grammar",
        "Complex kanji",
        "Academic reading",
        "Business Japanese"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(239, 68, 68) 0%, rgb(236, 72, 153) 100.2%)"
    },
    {
      title: "N2",
      description: "Upper-intermediate level for daily life and work situations",
      icon: "📚",
      link: "/jlpt/n2",
      color: "from-orange-500 to-red-600",
      features: [
        "Intermediate grammar",
        "Business basics",
        "News reading",
        "Conversation skills"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(255, 126, 95) 0%, rgb(254, 180, 123) 100.2%)"
    },
    {
      title: "N3",
      description: "Intermediate level for everyday communication",
      icon: "💬",
      link: "/jlpt/n3",
      color: "from-yellow-500 to-orange-600",
      features: [
        "Basic grammar",
        "Daily conversation",
        "Simple reading",
        "Cultural context"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(234, 179, 8) 0%, rgb(251, 146, 60) 100.2%)"
    },
    {
      title: "N4",
      description: "Basic level for fundamental Japanese communication",
      icon: "🎨",
      link: "/jlpt/n4",
      color: "from-green-500 to-yellow-600",
      features: [
        "Basic grammar",
        "Simple kanji",
        "Daily expressions",
        "Basic reading"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(34, 197, 94) 0%, rgb(234, 179, 8) 100.2%)"
    },
    {
      title: "N5",
      description: "Beginner level for basic Japanese understanding",
      icon: "🌱",
      link: "/jlpt/n5",
      color: "from-blue-500 to-green-600",
      features: [
        "Basic expressions",
        "Simple grammar",
        "Hiragana/Katakana",
        "Basic vocabulary"
      ],
      bgPattern: "radial-gradient(circle at 10% 20%, rgb(59, 130, 246) 0%, rgb(34, 197, 94) 100.2%)"
    }
  ];

  return (
    <div className="min-h-screen pt-24 relative overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-50 via-gray-100 to-gray-200">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full mix-blend-multiply filter blur-xl opacity-20"
            style={{
              width: 100 + Math.random() * 300,
              height: 100 + Math.random() * 300,
              background: i % 3 === 0 
                ? 'rgba(59, 130, 246, 0.2)' 
                : i % 3 === 1 
                  ? 'rgba(139, 92, 246, 0.2)' 
                  : 'rgba(236, 72, 153, 0.2)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
            animate={{
              x: [0, Math.random() * 100 - 50],
              y: [0, Math.random() * 100 - 50],
              opacity: [0.2, 0.4, 0.2]
            }}
            transition={{
              duration: 15 + Math.random() * 15,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'easeInOut',
              delay: Math.random() * 5
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="text-center mb-32"
        >
          <motion.div 
            className="inline-block mb-6"
            animate={{ 
              y: [0, -10, 0]
            }}
            transition={{ 
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <h2 className="text-6xl md:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary drop-shadow-lg">
            JLPT/NAT/JLCT/J-cert Preparation
            </h2>
          </motion.div>
          <motion.div
            className="h-1 w-24 bg-gradient-to-r from-primary to-secondary mx-auto mb-6 rounded-full"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          />
          <motion.p 
            className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto font-medium"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            Comprehensive preparation for N4 & N5 levels with expert guidance and practice materials.
          </motion.p>
        </motion.div>

        {/* JLPT Levels */}
        <div className="relative">
          {/* Mobile Layout */}
          <div className="md:hidden space-y-6">
            {levels.map((level, index) => (
              <motion.div
                key={level.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="w-full"
              >
                <motion.div
                  className="relative w-full cursor-pointer"
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setActiveLevel(activeLevel === index ? null : index)}
                  onHoverStart={() => setHoveredLevel(index)}
                  onHoverEnd={() => setHoveredLevel(null)}
                >
                  <motion.div
                    className="absolute inset-0 rounded-3xl"
                    style={{
                      background: level.bgPattern,
                      opacity: hoveredLevel === index ? 0.2 : 0.1,
                    }}
                    animate={{
                      scale: hoveredLevel === index ? 1.05 : 1,
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  <div className="relative p-6 rounded-3xl bg-white/95 backdrop-blur-sm shadow-xl border border-gray-100">
                    <motion.div 
                      className="text-4xl mb-4"
                      animate={hoveredLevel === index ? {
                        scale: [1, 1.2, 1]
                      } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {level.icon}
                    </motion.div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">{level.title}</h3>
                    <p className="text-sm text-gray-600">{level.description}</p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:block relative max-w-7xl mx-auto">
            {/* Rotating Circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="w-[1200px] h-[1200px] rounded-full border-2 border-dashed border-gray-300/50"
                animate={{
                  rotate: [0, 360]
                }}
                transition={{
                  duration: 60,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </div>

            {/* JLPT Levels */}
            <div className="relative h-[1000px]">
              {levels.map((level, index) => {
                const angle = ((index * 360) / levels.length) + 270;
                const radius = 450;
                const x = Math.cos((angle * Math.PI) / 180) * radius;
                const y = Math.sin((angle * Math.PI) / 180) * radius;

                return (
                  <motion.div
                    key={level.title}
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                    className="absolute"
                    style={{
                      left: `calc(50% + ${x}px)`,
                      top: `calc(50% + ${y}px)`,
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <motion.div
                      className="relative w-80 cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      onClick={() => setActiveLevel(activeLevel === index ? null : index)}
                      onHoverStart={() => setHoveredLevel(index)}
                      onHoverEnd={() => setHoveredLevel(null)}
                    >
                      <motion.div
                        className="absolute inset-0 rounded-3xl"
                        style={{
                          background: level.bgPattern,
                          opacity: hoveredLevel === index ? 0.2 : 0.1,
                        }}
                        animate={{
                          scale: hoveredLevel === index ? 1.05 : 1,
                        }}
                        transition={{ duration: 0.3 }}
                      />
                      <div className="relative p-8 rounded-3xl bg-white/95 backdrop-blur-sm shadow-xl border border-gray-100">
                        <motion.div 
                          className="text-5xl mb-6"
                          animate={hoveredLevel === index ? {
                            scale: [1, 1.2, 1]
                          } : {}}
                          transition={{ duration: 0.5 }}
                        >
                          {level.icon}
                        </motion.div>
                        <h3 className="text-2xl font-bold mb-3 text-gray-900">{level.title}</h3>
                        <p className="text-base text-gray-600">{level.description}</p>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Level Details Modal */}
        <AnimatePresence>
          {activeLevel !== null && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            >
              <motion.div
                className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
              >
                <motion.div
                  className="absolute inset-0 rounded-3xl"
                  style={{
                    background: levels[activeLevel].bgPattern,
                    opacity: 0.1
                  }}
                />
                <div className="relative p-6 md:p-8">
                  <button
                    onClick={() => setActiveLevel(null)}
                    className="absolute top-4 right-4 w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                  >
                    <svg
                      className="w-5 h-5 md:w-6 md:h-6 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6 text-gray-900">{levels[activeLevel].title} Level</h3>
                  <p className="text-lg md:text-xl text-gray-700 mb-8 md:mb-12">{levels[activeLevel].description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                    {levels[activeLevel].features.map((feature, i) => (
                      <motion.div
                        key={i}
                        className="flex items-center text-gray-700"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                      >
                        <motion.div
                          className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-gradient-to-r from-primary to-secondary mr-3 md:mr-4"
                          animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.5, 1, 0.5]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: i * 0.2
                          }}
                        />
                        <span className="text-base md:text-lg">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                  <Link to={levels[activeLevel].link} className="mt-8 md:mt-12 inline-block">
                    <motion.div
                      className="inline-flex items-center text-gray-900 font-semibold text-base md:text-lg"
                      whileHover={{ x: 5 }}
                    >
                      Start Preparation
                      <motion.svg 
                        className="w-5 h-5 md:w-6 md:h-6 ml-2"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                        animate={{ 
                          x: [0, 5, 0]
                        }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </motion.svg>
                    </motion.div>
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-32"
        >
          <Link to="/contact">
            <motion.button
              className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white text-lg md:text-xl font-bold rounded-full relative overflow-hidden group mb-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
                animate={{
                  x: ['-100%', '100%']
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              Start Your Language Journey
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default JLPTPreparation; 